import { Box, makeStyles, Typography } from "@material-ui/core";
import React, { FC } from "react";

import { AgentCompatibilityRadar } from "@/api/generated";
import {
  CompatibilityRadarChart,
  hasCompatibilityRadarScores,
} from "@/components/compatibility_radar_chart/CompatibilityRadarChart";
import { CompatibilityRadarInfoButton } from "@/components/compatibility_radar_chart/CompatibilityRadarInfoModal";
import { useDevice } from "@/hooks/useDevice";
import theme from "@/theme";

import { CompatibilityScoreDisplay } from "./CompatibilityScoreDisplay";

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: theme.palette.semantic["surface/base"],
    padding: 24,
    display: "flex",
    flexDirection: "column",
    gap: 12,
    [theme.breakpoints.up("md")]: { padding: 0 },
  },
  headingRow: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 },
  heading: {
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.semantic["text/heading"],
    [theme.breakpoints.up("md")]: { fontSize: 20 },
  },
  // Figma B-1: 見出しの下に白い枠で「あなたとの相性スコア」と数値を並べる
  highlight: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 16,
    padding: 12,
    borderRadius: 8,
    backgroundColor: theme.palette.semantic["surface/card"],
  },
  highlightLabel: { fontSize: 14, fontWeight: 700, lineHeight: 1.5, color: theme.palette.semantic["text/heading"] },
  chart: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    [theme.breakpoints.up("md")]: {
      backgroundColor: theme.palette.semantic["surface/card"],
      padding: 24,
      borderRadius: 16,
    },
  },
}));

/**
 * プランナー詳細とメッセージ内のプランナー情報で同じ相性表示を使う。
 * rank は遷移元の一覧での順位（相性プランナー一覧＝診断結果・ホーム、または検索一覧）。上位3人だけ王冠が付き、順位が無い経路（オファー・メッセージ・直リンクなど）では付かない。
 */
export const AgentCompatibilitySection: FC<{
  radar?: AgentCompatibilityRadar | null;
  className?: string;
  variant?: "default" | "certified";
  rank?: number;
}> = ({ radar, className, variant = "default", rank }) => {
  const classes = useStyles();
  const { isMobile } = useDevice();
  if (!hasCompatibilityRadarScores(radar?.scores)) return null;
  const isCertified = variant === "certified";
  const hasScore = typeof radar?.total_score === "number" && Number.isFinite(radar.total_score);

  return (
    <Box className={`${classes.root} ${className ?? ""}`} data-testid="planner-compatibility-section">
      <Box className={classes.headingRow}>
        <Typography component="h2" className={classes.heading}>あなたとの相性</Typography>
        {!isCertified && hasScore && (
          <CompatibilityScoreDisplay score={radar.total_score} showCrown={false} />
        )}
      </Box>
      {isCertified && hasScore && (
        <Box className={classes.highlight} data-testid="planner-detail-score-highlight">
          <Typography className={classes.highlightLabel}>あなたとの相性スコア</Typography>
          <CompatibilityScoreDisplay score={radar.total_score} rank={rank} size="L" crownSize={24} />
        </Box>
      )}
      <Box className={classes.chart}>
        <CompatibilityRadarChart scores={radar?.scores} size="full" variant="planner" />
        {/* SP は base の帯に白い行、PC は白いグラフカードに base の行を置く。 */}
        <CompatibilityRadarInfoButton variant="row" surface={isMobile ? "card" : "base"} />
      </Box>
    </Box>
  );
};
